import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import TextButton from 'LuxKit/Button/TextButton'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getPriceDisplayFunction, hasSelectedRooms } from 'lib/offer/offerPageUtil'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useExperiencesPassportForOffers } from 'components/Experiences/ExperiencesPassport/useExperiencesPassportForOffer'
import { arePropertyRatesLoading, getPropertyCombinedRatesByRoom, getPropertyRatesKey } from 'selectors/offerPage/offerPageSelectors'
import {
  getActivePropertyRatesByRoom,
  type PropertyRateByRoom,
} from 'selectors/offerPage/propertyMultipleRoomBookingSelectors'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { getSource } from 'selectors/offerSelectors'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { selectLoggedIn } from 'selectors/accountSelectors'

import { propertyMultiRoomBookNowOrAddToCartCallback } from './propertyMultiRoomBuyCallback'
import { getPropertyRatePassportOffer } from './singlePropertyUtils'

const FooterContainer = styled(Pane)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

interface Props {
  property: App.Property.Property
  hasBookableRooms: boolean
  hasRatesKey: boolean
  rooms: Array<OfferPageRoom>
  activeRatesByRoom: PropertyRateByRoom
  isLoading: boolean
  isFlightsSelected: boolean
  duration: number
  priceMode: OfferPagePriceMode
  airport: App.Airport | undefined
  checkIn?: string
  checkOut?: string
  regionCode: string
  currentCurrency: string
  airports?: Array<App.Airport>
  memberId?: string
  source: string
  isLoggedIn: boolean
  isSpoofed: boolean
  subscriber: App.OrderSubscriber | undefined
}

function PropertyMultiIndividualRoomsFooter(props: Props) {
  const {
    property,
    hasBookableRooms,
    hasRatesKey,
    rooms = EmptyArray,
    activeRatesByRoom,
    isLoading,
    isFlightsSelected,
    duration,
    priceMode,
    airport,
    checkIn,
    checkOut,
    regionCode,
    currentCurrency,
    airports,
    memberId,
    source,
    isLoggedIn,
    isSpoofed,
    subscriber,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)

  const selectedRates = useMemo(() => {
    return rooms.map(room => activeRatesByRoom[room.id]).filter((rate): rate is App.EnrichedPropertyRate => !!rate)
  }, [activeRatesByRoom, rooms])
  const selectedRoomRates = useMemo(() => {
    return rooms.map(room => {
      const rate = activeRatesByRoom[room.id]
      if (!rate) {
        return undefined
      }
      return { room, rate }
    }).filter((entry): entry is { room: OfferPageRoom, rate: App.EnrichedPropertyRate } => !!entry)
  }, [rooms, activeRatesByRoom])
  const passportOffers = useMemo(() => selectedRoomRates.map(({ room, rate }) => ({
    key: room.id,
    offer: getPropertyRatePassportOffer(rate, property),
  })), [property, selectedRoomRates])
  const { byKey: passportByRoom, fetching: experiencesPassportFetching } = useExperiencesPassportForOffers({
    offers: passportOffers,
    filters: { checkIn, checkOut },
  })

  const hasAllRoomSelections = rooms.length > 0 && selectedRates.length === rooms.length

  const totals = useMemo(() => {
    let hotelPrice = 0
    let hotelValue = 0
    let hotelMemberPrice = 0
    for (const rate of selectedRates) {
      hotelPrice += rate.totals.displayPrice
      hotelValue += rate.totals.valuedUpToPrice ?? rate.totals.displayPrice
      hotelMemberPrice += rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice
    }
    return { hotelPrice, hotelValue, hotelMemberPrice }
  }, [selectedRates])
  const selectedRatesFlightQuotedPrice = useMemo((): number | undefined => {
    if (!isFlightsSelected) {
      return undefined
    }
    let quotedPrice = 0
    for (const rate of selectedRates) {
      if (!rate.includesFlights) {
        continue
      }
      if (typeof rate.cheapestFlightPrice !== 'number') {
        return undefined
      }
      quotedPrice += rate.cheapestFlightPrice
    }
    return quotedPrice > 0 ? Number(quotedPrice.toFixed(2)) : undefined
  }, [isFlightsSelected, selectedRates])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)
  const discountPercentage = getValueOffPercent(totals.hotelValue, totals.hotelPrice)
  const shouldShowValue = totals.hotelValue > totals.hotelPrice
  const shouldShowDiscount = shouldShowValue && discountPercentage > MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const nightsSaleUnitsFlightsFromCaption = intl.formatMessage(
    {
      id: '2ISm1hFXFJbK_BFNm03Y',
      defaultMessage: '{nightCount, plural, one {# night} other {# nights}}, {roomCount, plural, one {# room} other {# rooms}} + flights from',
      description: 'Price caption in the multi-room footer on the offer detail page when flights are included',
    },
    { nightCount: duration, roomCount: rooms.length },
  )
  const nightsSaleUnitsFromCaption = intl.formatMessage(
    {
      id: 'QS5Ui_cB2_TT4U2ULNLY',
      defaultMessage: '{nightCount, plural, one {# night} other {# nights}}, {roomCount, plural, one {# room} other {# rooms}} from',
      description: 'Price caption in the multi-room footer on the offer detail page for the hotel only price',
    },
    { nightCount: duration, roomCount: rooms.length },
  )

  const bookNowLabel = intl.formatMessage({
    id: 'QtRwxjRixW1FwB_2XnzB',
    defaultMessage: 'Book now',
    description: 'Primary CTA button text to book property rate option',
  })
  const addToCartLabel = intl.formatMessage({
    id: 'Sp8Q-ZMQBm-e_QqcwVuz',
    defaultMessage: 'Add to cart',
    description: 'Secondary CTA button text to add property rate option to cart',
  })
  // The multi-room footer only renders for two or more occupancy rooms, so this CTA covers a
  // package per room. It keeps its own message rather than the singular one shared with
  // PropertyAvailableRateOptionCtas.
  const selectPackagesLabel = intl.formatMessage({
    id: 'F9lDy8fCptqqrFk4jcvf',
    defaultMessage: 'Select packages',
    description: 'CTA text in the multi-room footer when flights are included',
  })
  const chooseDatesLabel = intl.formatMessage({
    id: 'beXZCjk737HxfL2dx7zx',
    defaultMessage: 'Check availability',
    description: 'CTA shown in the SPP multi-room footer when no check-in date is set',
  })

  const noDatesSelected = !hasRatesKey
  const canShowPrice = !noDatesSelected && hasAllRoomSelections && !isLoading && totals.hotelPrice > 0
  const showLoading = isLoading
  const isFlightPricingReady = !isFlightsSelected || selectedRatesFlightQuotedPrice !== undefined
  const ctaDisabled = noDatesSelected ? false : (showLoading || !hasAllRoomSelections || !isFlightPricingReady || experiencesPassportFetching)

  const onFooterCtaClick = useCallback(async(shouldAddToCart: boolean) => {
    if (!(checkIn && checkOut)) {
      offerPageDispatch({
        type: OfferPageActions.toggleFilter,
        filter: hasSelectedRooms(rooms) ? 'checkin' : 'capacity',
      })
      return
    }
    if (isFlightsSelected && selectedRatesFlightQuotedPrice === undefined) {
      return
    }

    trackOfferPageEvent('BOOK_NOW_PRESSED')

    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    const experiencesPassportCheckoutSourcesByRoom = Object.fromEntries(
      Object.entries(passportByRoom).map(([roomId, result]) => [roomId, result.checkoutSources]),
    )

    await propertyMultiRoomBookNowOrAddToCartCallback({
      shouldAddToCart,
      dispatch,
      currentCurrency,
      airports,
      memberId,
      offerPageDispatch,
      modalElementContext,
      showModal,
      intl,
      property,
      selectedRatesByRoom: selectedRoomRates,
      originAirportCode: airport?.code,
      flightQuotedPrice: selectedRatesFlightQuotedPrice,
      checkIn,
      checkOut,
      duration,
      regionCode,
      source,
      isSpoofed,
      subscriber,
      experiencesPassportCheckoutSourcesByRoom,
    })
  }, [
    airport?.code,
    airports,
    checkIn,
    checkOut,
    currentCurrency,
    dispatch,
    duration,
    intl,
    isFlightsSelected,
    isLoggedIn,
    isSpoofed,
    modalElementContext,
    memberId,
    offerPageDispatch,
    property,
    regionCode,
    rooms,
    selectedRoomRates,
    selectedRatesFlightQuotedPrice,
    showModal,
    source,
    subscriber,
    trackOfferPageEvent,
    passportByRoom,
  ])

  const onBookNowClick = useCallback(() => {
    onFooterCtaClick(false)
  }, [onFooterCtaClick])

  const onAddToCartClick = useCallback(() => {
    onFooterCtaClick(true)
  }, [onFooterCtaClick])

  if (!hasBookableRooms) return null

  return (
    <FooterContainer>
      <Group direction="vertical" gap={20} desktopDirection="horizontal-reverse" desktopHorizontalAlign="space-between" verticalAlign="center">
        <Group direction="vertical" gap={20} desktopDirection="horizontal" verticalAlign="center">
          {canShowPrice && (
            <div>
              <PriceRowPriceCaption>
                {isFlightsSelected ? nightsSaleUnitsFlightsFromCaption : nightsSaleUnitsFromCaption}
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={convertToDisplay(totals.hotelPrice)}
                memberPrice={convertToDisplay(totals.hotelMemberPrice)}
                saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                testid="pricing"
              />
              {shouldShowValue && (
                <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={convertToDisplay(totals.hotelValue)}
                  discountPercentage={shouldShowDiscount ? discountPercentage : undefined}
                />
              )}
            </div>
          )}
          <Group direction="horizontal" gap={12} horizontalAlign="stretch">
            {noDatesSelected && (
              <TextButton kind="primary" size="large" onClick={onBookNowClick}>
                {chooseDatesLabel}
              </TextButton>
            )}
            {!noDatesSelected && isFlightsSelected && (
              <TextButton kind="primary" size="large" onClick={onBookNowClick} disabled={ctaDisabled}>
                {selectPackagesLabel}
              </TextButton>
            )}
            {!noDatesSelected && !isFlightsSelected && (
              <>
                <TextButton kind="primary" size="large" onClick={onBookNowClick} disabled={ctaDisabled}>
                  {bookNowLabel}
                </TextButton>
                <TextButton kind="secondary" size="large" onClick={onAddToCartClick} disabled={ctaDisabled}>
                  {addToCartLabel}
                </TextButton>
              </>
            )}
          </Group>
        </Group>
        {showLoading && <LoadingIndicator inline />}
      </Group>
    </FooterContainer>
  )
}

type MappedProps = Pick<Props,
  | 'property'
  | 'hasBookableRooms'
  | 'hasRatesKey'
  | 'rooms'
  | 'activeRatesByRoom'
  | 'isLoading'
  | 'isFlightsSelected'
  | 'duration'
  | 'priceMode'
  | 'airport'
  | 'checkIn'
  | 'checkOut'
  | 'regionCode'
  | 'currentCurrency'
  | 'airports'
  | 'memberId'
  | 'source'
  | 'isLoggedIn'
  | 'isSpoofed'
  | 'subscriber'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  return {
    property: state.property!, // Guaranteed by `<PropertyContainer/>`.
    hasRatesKey: !!getPropertyRatesKey(state, appState),
    hasBookableRooms: getPropertyCombinedRatesByRoom(state, appState).length > 0,
    rooms: state.rooms ?? EmptyArray,
    activeRatesByRoom: getActivePropertyRatesByRoom(state, appState),
    isLoading: arePropertyRatesLoading(state, appState),
    isFlightsSelected: state.flights,
    duration: state.duration,
    priceMode: state.priceMode,
    airport: state.airport,
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    regionCode: appState.geo.currentRegionCode,
    currentCurrency: appState.geo.currentCurrency,
    airports: appState.geo.airports,
    memberId: appState.auth.account.memberId,
    source: getSource(appState),
    isLoggedIn: selectLoggedIn(appState),
    isSpoofed: appState.auth.account.isSpoofed,
    subscriber: getEnquirySubscriber(appState),
  }
}, PropertyMultiIndividualRoomsFooter)
